import {
  WORK_ORDER_OPEN_STATUSES,
  WORK_ORDER_SEVERITIES,
  WORK_ORDER_SEVERITY_LABELS,
  WORK_ORDER_STATUSES,
  WORK_ORDER_STATUS_LABELS,
  type WorkOrderSeverity,
  type WorkOrderStatus,
} from '@sitewise/shared';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  SeverityBadge,
  cn,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ClipboardList, Plus, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { fetchMe } from '../../../api/auth.js';
import {
  bulkTransitionWorkOrders,
  listWorkOrders,
  type WorkOrderSummary,
} from '../../../api/work-orders.js';
import { ApiError } from '../../../lib/api-client.js';

import { CreateWorkOrderSheet } from './create-sheet.js';
import { isOverdue, relativeTime, StatusPill } from './lib.js';
import { SavedViewsBar } from './saved-views-bar.js';

interface FilterState {
  q: string;
  status: WorkOrderStatus[];
  severity: WorkOrderSeverity[];
  mine: boolean;
  buildingId: string | null;
}

interface BuildingGroup {
  buildingId: string;
  buildingName: string;
  workOrders: WorkOrderSummary[];
}

const QUICK_FILTERS: { id: 'all' | 'mine' | 'open'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'mine', label: 'My open' },
];

export function WorkOrdersListPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const isTabletMode = meQuery.data?.user.tabletMode === true;
  const initial: FilterState = {
    q: params.get('q') ?? '',
    status: parseList(params.get('status')) as WorkOrderStatus[],
    severity: parseList(params.get('severity')) as WorkOrderSeverity[],
    mine: params.get('mine') === '1',
    buildingId: params.get('buildingId'),
  };
  const [filters, setFilters] = useState<FilterState>(initial);
  const [openCreate, setOpenCreate] = useState(params.get('new') === '1');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [cursor, setCursor] = useState<number>(-1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const searchRef = useRef<HTMLInputElement>(null);

  const queryKey = useMemo(() => ['work-orders', filters], [filters]);
  const woQuery = useQuery({
    queryKey,
    queryFn: () =>
      listWorkOrders({
        q: filters.q || undefined,
        status: filters.status.length > 0 ? filters.status : undefined,
        severity: filters.severity.length > 0 ? filters.severity : undefined,
        mine: filters.mine || undefined,
        buildingId: filters.buildingId ?? undefined,
        limit: 100,
      }),
    placeholderData: (prev) => prev,
  });

  const handleBulk = useMutation({
    mutationFn: (toStatus: WorkOrderStatus) =>
      bulkTransitionWorkOrders({
        ids: Array.from(selected),
        toStatus,
        notes: null,
      }),
    onSuccess: (data) => {
      toast.success(`${data.transitioned} work orders updated`);
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] });
    },
    onError: (err) => {
      toast.error(
        err instanceof ApiError ? err.message : 'Bulk transition failed',
      );
    },
  });

  const confirmBulk = (toStatus: WorkOrderStatus, label: string) => {
    if (selected.size === 0) return;
    const confirmed = window.confirm(
      `${label} ${selected.size} work order${selected.size === 1 ? '' : 's'}?\n\nThe whole batch must be valid; if any cannot move to ${WORK_ORDER_STATUS_LABELS[toStatus]} the change is rolled back.`,
    );
    if (confirmed) handleBulk.mutate(toStatus);
  };

  const updateFilters = useCallback(
    (patch: Partial<FilterState>) => {
      const next = { ...filters, ...patch };
      setFilters(next);
      const newParams = new URLSearchParams();
      if (next.q) newParams.set('q', next.q);
      if (next.status.length) newParams.set('status', next.status.join(','));
      if (next.severity.length) newParams.set('severity', next.severity.join(','));
      if (next.mine) newParams.set('mine', '1');
      if (next.buildingId) newParams.set('buildingId', next.buildingId);
      setParams(newParams, { replace: true });
    },
    [filters, setParams],
  );

  const toggleStatus = (s: WorkOrderStatus) => {
    updateFilters({
      status: filters.status.includes(s)
        ? filters.status.filter((v) => v !== s)
        : [...filters.status, s],
    });
  };

  const toggleSeverity = (s: WorkOrderSeverity) => {
    updateFilters({
      severity: filters.severity.includes(s)
        ? filters.severity.filter((v) => v !== s)
        : [...filters.severity, s],
    });
  };

  const setQuick = (id: 'all' | 'mine' | 'open') => {
    if (id === 'all') updateFilters({ mine: false, status: [] });
    if (id === 'mine') updateFilters({ mine: true, status: [...WORK_ORDER_OPEN_STATUSES] });
    if (id === 'open') updateFilters({ mine: false, status: [...WORK_ORDER_OPEN_STATUSES] });
  };

  const rows = useMemo(() => woQuery.data?.workOrders ?? [], [woQuery.data?.workOrders]);

  const groups = useMemo((): BuildingGroup[] => {
    const map = new Map<string, BuildingGroup>();
    for (const wo of rows) {
      const existing = map.get(wo.buildingId);
      if (existing) {
        existing.workOrders.push(wo);
      } else {
        map.set(wo.buildingId, {
          buildingId: wo.buildingId,
          buildingName: wo.buildingName ?? 'Building',
          workOrders: [wo],
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.buildingName.localeCompare(b.buildingName),
    );
  }, [rows]);

  // Expand all buildings whenever the grouped set changes (first load / filter).
  useEffect(() => {
    if (groups.length === 0) return;
    if (groups.length === 1 || filters.buildingId) {
      setExpanded(new Set(groups.map((g) => g.buildingId)));
      return;
    }
    setExpanded((prev) => {
      if (prev.size > 0) return prev;
      return new Set(groups.map((g) => g.buildingId));
    });
  }, [groups, filters.buildingId]);

  const allRowIds = useMemo(() => new Set(rows.map((r) => r.id)), [rows]);

  function toggleBuilding(buildingId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(buildingId)) next.delete(buildingId);
      else next.add(buildingId);
      return next;
    });
  }

  // Linear-style keyboard shortcuts on the list view.
  useEffect(() => {
    function shouldIgnore(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (target.isContentEditable) return true;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
    }
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (shouldIgnore(e.target)) return;
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor((c) => Math.min(rows.length - 1, c + 1));
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor((c) => Math.max(0, c - 1));
      } else if (e.key === 'Enter') {
        if (cursor >= 0 && cursor < rows.length) {
          e.preventDefault();
          navigate(`/work-orders/${rows[cursor]!.id}`);
        }
      } else if (e.key === 'c' && !e.shiftKey) {
        e.preventDefault();
        setOpenCreate(true);
      } else if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.shiftKey && (e.key === 'A' || e.key === 'a')) {
        if (selected.size === 0) return;
        e.preventDefault();
        const first = rows.find((r) => selected.has(r.id));
        if (first) navigate(`/work-orders/${first.id}?focus=assign`);
      } else if (e.shiftKey && (e.key === 'S' || e.key === 's')) {
        if (selected.size === 0) return;
        e.preventDefault();
        confirmBulk('in_progress', 'Start');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // selected and confirmBulk are intentionally captured at the time of
    // attachment; this matches the existing pattern in this file.
  }, [rows, cursor, navigate, selected]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the underlying rows change, clamp the cursor.
  useEffect(() => {
    if (cursor >= rows.length) setCursor(rows.length - 1);
  }, [rows.length, cursor]);
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allRowIds));
    }
  };

  const columns = [
    {
      id: 'select',
      description: 'Select work orders to act on them together.',
      header: (
        <input
          type="checkbox"
          aria-label="Select all"
          checked={allSelected}
          onChange={toggleAll}
        />
      ),
      cell: (row: WorkOrderSummary) => (
        <input
          type="checkbox"
          aria-label={`Select ${row.displayNumber}`}
          checked={selected.has(row.id)}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            setSelected((prev) => {
              const next = new Set(prev);
              if (next.has(row.id)) next.delete(row.id);
              else next.add(row.id);
              return next;
            });
          }}
        />
      ),
      className: 'w-10',
    },
    {
      id: 'number',
      header: '#',
      description: 'The work order number.',
      cell: (r: WorkOrderSummary) => (
        <span className="font-mono text-xs tabular-nums">{r.displayNumber}</span>
      ),
      className: 'w-32',
    },
    {
      id: 'title',
      header: 'Title',
      description: 'Short name of the work, with the description underneath when there is one.',
      cell: (r: WorkOrderSummary) => (
        <div className="flex flex-col">
          <span className="font-medium">{r.title}</span>
          {r.description ? (
            <span className="line-clamp-1 text-xs text-fg-secondary">{r.description}</span>
          ) : null}
        </div>
      ),
    },
    {
      id: 'severity',
      header: 'Severity',
      description: 'How urgent the work order is.',
      cell: (r: WorkOrderSummary) => <SeverityBadge level={r.severity} subtle />,
      className: 'w-28',
    },
    {
      id: 'status',
      header: 'Status',
      description: 'Where the work order is in its lifecycle, from open through closed.',
      cell: (r: WorkOrderSummary) => <StatusPill status={r.status} />,
      className: 'w-40',
    },
    {
      id: 'due',
      header: 'Due',
      description: 'The date the work is due. Overdue dates are highlighted.',
      cell: (r: WorkOrderSummary) =>
        r.dueAt ? (
          <span
            className={cn(
              'text-xs',
              isOverdue(r.dueAt, r.status) ? 'font-medium text-danger' : 'text-fg-secondary',
            )}
          >
            {new Date(r.dueAt).toLocaleDateString()}
          </span>
        ) : (
          <span className="text-xs text-fg-muted">—</span>
        ),
      className: 'w-28',
    },
    {
      id: 'updated',
      header: 'Updated',
      description: 'When the work order was last changed.',
      cell: (r: WorkOrderSummary) => (
        <span className="text-xs text-fg-secondary">{relativeTime(r.updatedAt)}</span>
      ),
      className: 'w-32',
    },
  ];

  return (
    <>
      <PageHeader
        title="Work orders"
        description={
          isTabletMode
            ? 'Wall display — open a work order to view it. Close from a phone login.'
            : 'Maintenance, inspections, and follow-ups across your portfolio.'
        }
        actions={
          isTabletMode ? undefined : (
            <Button onClick={() => setOpenCreate(true)}>
              <Plus className="h-4 w-4" />
              New work order
            </Button>
          )
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-md border border-border bg-surface p-0.5">
          {QUICK_FILTERS.map((f) => {
            const active =
              (f.id === 'mine' && filters.mine) ||
              (f.id === 'open' && !filters.mine && filters.status.length === WORK_ORDER_OPEN_STATUSES.length) ||
              (f.id === 'all' && !filters.mine && filters.status.length === 0);
            return (
              <button
                key={f.id}
                onClick={() => setQuick(f.id)}
                className={cn(
                  'rounded px-3 py-1 text-xs font-medium transition-colors',
                  active ? 'bg-primary-subtle text-primary' : 'text-fg-secondary hover:text-fg',
                )}
              >
                {f.label}
              </button>
            );
          })}
        </div>
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" />
          <Input
            ref={searchRef}
            value={filters.q}
            onChange={(e) => updateFilters({ q: e.target.value })}
            placeholder='Search… ("/" to focus)'
            className="pl-8"
          />
        </div>
        <span className="text-xs text-fg-muted">
          <kbd className="rounded border border-border bg-surface px-1 text-[10px]">j</kbd>
          <kbd className="ml-0.5 rounded border border-border bg-surface px-1 text-[10px]">k</kbd>
          {' '}navigate ·{' '}
          <kbd className="rounded border border-border bg-surface px-1 text-[10px]">Enter</kbd>
          {' '}open ·{' '}
          <kbd className="rounded border border-border bg-surface px-1 text-[10px]">c</kbd>
          {' '}new
        </span>
        <div className="ml-auto flex items-center gap-2">
          <SavedViewsBar
            scope="work_orders"
            currentFilters={filters as unknown as Record<string, unknown>}
            onApply={(view) => {
              const f = view.filters as Partial<FilterState>;
              updateFilters({
                q: typeof f.q === 'string' ? f.q : '',
                status: Array.isArray(f.status) ? (f.status as WorkOrderStatus[]) : [],
                severity: Array.isArray(f.severity) ? (f.severity as WorkOrderSeverity[]) : [],
                mine: typeof f.mine === 'boolean' ? f.mine : false,
              });
            }}
          />
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-fg-muted">Status:</span>
        {WORK_ORDER_STATUSES.map((s) => {
          const active = filters.status.includes(s);
          const count = woQuery.data?.statusCounts[s] ?? 0;
          return (
            <button
              key={s}
              onClick={() => toggleStatus(s)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-sm px-2 py-0.5 text-xs',
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-surface-muted text-fg-secondary hover:bg-surface',
              )}
            >
              <span>{WORK_ORDER_STATUS_LABELS[s]}</span>
              <span className="rounded-sm bg-black/10 px-1 text-[10px] tabular-nums">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-fg-muted">Severity:</span>
        {WORK_ORDER_SEVERITIES.map((s) => {
          const active = filters.severity.includes(s);
          return (
            <button
              key={s}
              onClick={() => toggleSeverity(s)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-sm px-2 py-0.5 text-xs',
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-surface-muted text-fg-secondary hover:bg-surface',
              )}
            >
              <SeverityBadge level={s} dotOnly />
              <span>{WORK_ORDER_SEVERITY_LABELS[s]}</span>
            </button>
          );
        })}
      </div>

      {selected.size > 0 ? (
        <div className="mb-3 flex items-center justify-between rounded-md border border-border bg-surface-elevated px-3 py-2 text-sm">
          <span>
            <strong className="tabular-nums">{selected.size}</strong> selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => confirmBulk('triaged', 'Mark triaged')}
              disabled={handleBulk.isPending}
            >
              Mark triaged
            </Button>
            {!isTabletMode ? (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => confirmBulk('completed', 'Mark complete')}
                  disabled={handleBulk.isPending}
                >
                  Mark complete
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => confirmBulk('cancelled', 'Cancel')}
                  disabled={handleBulk.isPending}
                >
                  Cancel
                </Button>
              </>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelected(new Set())}
              disabled={handleBulk.isPending}
            >
              Clear
            </Button>
          </div>
        </div>
      ) : null}

      {woQuery.isError ? (
        <ErrorState
          description={
            woQuery.error instanceof ApiError
              ? woQuery.error.message
              : 'We could not load work orders.'
          }
        />
      ) : woQuery.isLoading ? (
        <DataTable columns={columns} rows={[]} rowKey={(r) => r.id} isLoading />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title="No work orders yet"
          description="Submit one manually, or wait for an inspection or violation to spawn one."
          action={
            <Button onClick={() => setOpenCreate(true)} size="sm">
              <Plus className="h-4 w-4" />
              New work order
            </Button>
          }
        />
      ) : groups.length === 1 ? (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/work-orders/${r.id}`)}
          rowClassName={(r) => {
            const idx = rows.indexOf(r);
            return idx === cursor ? 'ring-1 ring-primary/40 ring-inset' : undefined;
          }}
        />
      ) : (
        <div className="space-y-3">
          {groups.map((group) => {
            const isOpen = expanded.has(group.buildingId);
            return (
              <div
                key={group.buildingId}
                className="overflow-hidden rounded-lg border border-border bg-surface"
              >
                <button
                  type="button"
                  onClick={() => toggleBuilding(group.buildingId)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-surface-muted"
                >
                  <div className="min-w-0">
                    <div className="truncate font-semibold">{group.buildingName}</div>
                    <div className="text-xs text-fg-muted">
                      {group.workOrders.length} work order
                      {group.workOrders.length === 1 ? '' : 's'}
                    </div>
                  </div>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 shrink-0 text-fg-muted transition-transform',
                      isOpen ? 'rotate-0' : '-rotate-90',
                    )}
                  />
                </button>
                {isOpen ? (
                  <div className="border-t border-border">
                    <DataTable
                      columns={columns}
                      rows={group.workOrders}
                      rowKey={(r) => r.id}
                      onRowClick={(r) => navigate(`/work-orders/${r.id}`)}
                      rowClassName={(r) => {
                        const idx = rows.indexOf(r);
                        return idx === cursor
                          ? 'ring-1 ring-primary/40 ring-inset'
                          : undefined;
                      }}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <CreateWorkOrderSheet
        open={openCreate}
        onOpenChange={setOpenCreate}
        onCreated={(wo) => {
          setOpenCreate(false);
          navigate(`/work-orders/${wo.id}`);
        }}
      />
    </>
  );
}

function parseList(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}
